import { PaymentMethods } from '@websky/graphql';

import { DeepPartial } from '../types';
import { CardPaymentMethodWithUrl, PaymentPage, PaymentType } from './types';

const PAYMENT_KEY_TO_TYPE_MAP = new Map<keyof PaymentMethods, PaymentType>([
	['card', PaymentType.Card],
	['credit', PaymentType.Credit],
	['kaspi', PaymentType.Kaspi],
	['itransfer', PaymentType.Itransfer],
	['gpay', PaymentType.Gpay],
	['fps', PaymentType.Fps],
	['sbol', PaymentType.Sbol],
	['cash', PaymentType.Cash],
	['invoice', PaymentType.Invoice],
	['qiwi', PaymentType.Qiwi],
	['podeli', PaymentType.Podeli],
	['ffpBonus', PaymentType.FFPBonus],
	['uni_qr', PaymentType.UniQr]
]);

export const getSinglePaymentPage = (
	methods: DeepPartial<PaymentMethods>,
	options?: { includeCash?: boolean }
): PaymentPage | null => {
	const includeCash = options?.includeCash ?? false;
	const available: PaymentType[] = [];

	(Object.keys(methods) as Array<keyof PaymentMethods>).forEach(key => {
		const value = methods[key];

		if (typeof value !== 'object' || value === null || (key === 'cash' && !includeCash)) {
			return;
		}

		const type = PAYMENT_KEY_TO_TYPE_MAP.get(key);

		if (type) {
			available.push(type);
		}
	});

	if (available.length !== 1) {
		return null;
	}

	return {
		type: available[0],
		content: null,
		title: ''
	};
};

/**
 * WEBSKY-7271
 * накосячили в настройках шлюза и стало приходить "frameUrl": "?session="
 * из-за этого отображалась форма поиска внутри фрейма
 */
export const isCorrectFrameUrl = (frameInfo?: Pick<CardPaymentMethodWithUrl, 'frameUrl'>) => {
	return !!frameInfo?.frameUrl && frameInfo.frameUrl !== '?session=';
};

export const isPaymentMethodReadyToOpen = (
	type: PaymentType,
	methods: DeepPartial<PaymentMethods>,
	testPaymentEnabled: boolean
): boolean => {
	switch (type) {
		case PaymentType.Card:
			return isCorrectFrameUrl(methods.card);
		case PaymentType.Credit:
			return isCorrectFrameUrl(methods.credit);
		case PaymentType.Itransfer:
			return isCorrectFrameUrl(methods.itransfer);
		case PaymentType.Fps:
			return isCorrectFrameUrl(methods.fps);
		case PaymentType.Sbol:
			return isCorrectFrameUrl(methods.sbol);
		case PaymentType.Podeli:
			return isCorrectFrameUrl(methods.podeli);
		case PaymentType.FFPBonus:
			return isCorrectFrameUrl(methods.ffpBonus);
		case PaymentType.UniQr:
			return isCorrectFrameUrl(methods.uni_qr);
		case PaymentType.Kaspi:
			return !!methods.kaspi?.paymentCode;
		case PaymentType.Qiwi:
			return !!methods.qiwi?.paymentCode;
		case PaymentType.Gpay:
			return !!methods.gpay?.token;
		case PaymentType.Cash:
			return testPaymentEnabled && !!methods.cash;
		case PaymentType.Invoice:
			return !!methods.invoice;
		default:
			return false;
	}
};
